import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../../components/Badge';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/forms/Checkbox';
import { Input } from '../../../../components/forms/Input';
import { Select } from '../../../../components/forms/Select';
import { Modal } from '../../../../components/Modal';
import { ExceptionsEditor } from '../components/ExceptionsEditor';
import { ShowInactiveToggle } from '../components/ShowInactiveToggle';
import { WeeklyHoursEditor } from '../components/WeeklyHoursEditor';
import { useResourceMutations, useResources } from '../hooks';
import {
  isProtectedByBookings,
  Resource,
  ResourceKind,
  SchedulingApiError,
  SchedulingProfile,
} from '../types';

interface FormState {
  id: string | null;
  name: string;
  kind: ResourceKind;
  priority: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = { id: null, name: '', kind: 'staff', priority: '0', isActive: true };

export const ResourcesTab: React.FC<{ profile: SchedulingProfile }> = ({ profile }) => {
  const { t } = useTranslation();
  const { data: resources = [], isLoading } = useResources(profile.id);
  const mutations = useResourceMutations(profile.id);

  const [form, setForm] = useState<FormState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [scheduleFor, setScheduleFor] = useState<Resource | null>(null);
  const [toDelete, setToDelete] = useState<Resource | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const kindOptions: { value: ResourceKind; label: string }[] = [
    { value: 'staff', label: t('schedulingAdmin.resources.kinds.staff') },
    { value: 'room', label: t('schedulingAdmin.resources.kinds.room') },
    { value: 'equipment', label: t('schedulingAdmin.resources.kinds.equipment') },
  ];

  const openForm = (resource?: Resource) => {
    setFieldErrors({});
    setFormError(null);
    setForm(
      resource
        ? {
            id: resource.id,
            name: resource.name,
            kind: resource.kind,
            priority: String(resource.priority),
            isActive: resource.is_active,
          }
        : EMPTY_FORM
    );
  };

  const handleSave = async () => {
    if (!form) return;
    const data = {
      name: form.name.trim(),
      kind: form.kind,
      priority: Number(form.priority) || 0,
      is_active: form.isActive,
    };
    setFieldErrors({});
    setFormError(null);
    try {
      if (form.id) {
        await mutations.update.mutateAsync({ id: form.id, data });
      } else {
        await mutations.create.mutateAsync(data);
      }
      setForm(null);
    } catch (error) {
      if (error instanceof SchedulingApiError) {
        setFieldErrors(error.fieldErrors);
        setFormError(error.nonFieldErrors[0] ?? null);
      } else {
        setFormError(t('schedulingAdmin.errors.generic'));
      }
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleteError(null);
    try {
      await mutations.remove.mutateAsync(toDelete.id);
      setToDelete(null);
    } catch (error) {
      // Resources with bookings are protected by the backend, deactivating is the alternative.
      if (isProtectedByBookings(error)) {
        setDeleteBlocked(true);
      } else {
        setDeleteError(
          error instanceof SchedulingApiError ? error.message : t('schedulingAdmin.errors.generic')
        );
      }
    }
  };

  const handleDeactivate = async () => {
    if (!toDelete) return;
    setDeleteError(null);
    try {
      await mutations.update.mutateAsync({ id: toDelete.id, data: { is_active: false } });
      setToDelete(null);
    } catch {
      setDeleteError(t('schedulingAdmin.errors.generic'));
    }
  };

  const inactiveCount = resources.filter(resource => !resource.is_active).length;
  const sorted = resources
    .filter(resource => showInactive || resource.is_active)
    .sort((a, b) => b.priority - a.priority || a.name.localeCompare(b.name));

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
          {t('schedulingAdmin.resources.intro')}
        </p>
        <Button onClick={() => openForm()}>{t('schedulingAdmin.resources.add')}</Button>
      </div>

      <ShowInactiveToggle count={inactiveCount} checked={showInactive} onChange={setShowInactive} />

      {isLoading ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : resources.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('schedulingAdmin.resources.empty')}
        </p>
      ) : (
        sorted.length > 0 && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
            {sorted.map(resource => (
              <div
                key={resource.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-medium text-gray-900 dark:text-white truncate">
                    {resource.name}
                  </span>
                  <Badge variant="secondary" size="sm">
                    {t(`schedulingAdmin.resources.kinds.${resource.kind}`)}
                  </Badge>
                  {!resource.is_active && (
                    <Badge variant="warning" size="sm">
                      {t('common.inactive')}
                    </Badge>
                  )}
                  <span className="text-xs text-gray-500">
                    {t('schedulingAdmin.resources.priorityValue', { value: resource.priority })}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setScheduleFor(resource)}>
                    {t('schedulingAdmin.resources.hoursAndAbsence')}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => openForm(resource)}>
                    {t('common.edit')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    title={t('common.delete')}
                    onClick={() => {
                      setDeleteError(null);
                      setDeleteBlocked(false);
                      setToDelete(resource);
                    }}
                  >
                    <span className="material-icons text-lg">delete</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <Modal
        isOpen={!!form}
        onClose={() => setForm(null)}
        title={
          form?.id
            ? t('schedulingAdmin.resources.editTitle')
            : t('schedulingAdmin.resources.addTitle')
        }
        size="md"
      >
        {form && (
          <div className="space-y-4">
            <Input
              label={t('schedulingAdmin.fields.name')}
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder={t('schedulingAdmin.resources.namePlaceholder')}
              errorText={fieldErrors.name}
              state={fieldErrors.name ? 'error' : 'default'}
              fullWidth
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('schedulingAdmin.resources.kind')}
              </label>
              <Select
                options={kindOptions}
                value={form.kind}
                onChange={value => setForm({ ...form, kind: value as ResourceKind })}
              />
            </div>
            <Input
              type="number"
              label={t('schedulingAdmin.resources.priority')}
              value={form.priority}
              onChange={e => setForm({ ...form, priority: e.target.value })}
              helperText={t('schedulingAdmin.resources.priorityHelp')}
              errorText={fieldErrors.priority}
              fullWidth
            />
            <Checkbox
              label={t('schedulingAdmin.resources.active')}
              helperText={t('schedulingAdmin.resources.activeHelp')}
              checked={form.isActive}
              onChange={e => setForm({ ...form, isActive: e.target.checked })}
            />
            {formError && <p className="text-sm text-red-600">{formError}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={() => setForm(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSave}
                loading={mutations.create.isPending || mutations.update.isPending}
                disabled={!form.name.trim()}
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!scheduleFor}
        onClose={() => setScheduleFor(null)}
        title={t('schedulingAdmin.resources.hoursTitle', { name: scheduleFor?.name })}
        size="xl"
      >
        {scheduleFor && (
          <div className="space-y-8">
            <section className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('schedulingAdmin.resources.hoursIntro')}
              </p>
              <WeeklyHoursEditor owner="resource" ownerId={scheduleFor.id} />
            </section>
            <section className="space-y-3">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {t('schedulingAdmin.exceptions.titleResource')}
              </h3>
              <ExceptionsEditor owner="resource" ownerId={scheduleFor.id} />
            </section>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        title={t('schedulingAdmin.resources.deleteTitle')}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {deleteBlocked
              ? t('schedulingAdmin.errors.protectedByBookings', { name: toDelete?.name })
              : t('schedulingAdmin.resources.deleteText', { name: toDelete?.name })}
          </p>
          {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setToDelete(null)}>
              {t('common.cancel')}
            </Button>
            {!deleteBlocked ? (
              <Button variant="danger" onClick={handleDelete} loading={mutations.remove.isPending}>
                {t('common.delete')}
              </Button>
            ) : (
              toDelete?.is_active && (
                <Button onClick={handleDeactivate} loading={mutations.update.isPending}>
                  {t('schedulingAdmin.errors.deactivateInstead')}
                </Button>
              )
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};
